import React from 'react';
import { 
  EyeIcon, 
  BoltIcon, 
  GlobeAltIcon, 
  SparklesIcon,
  ArrowRightIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';

const VisionMissionSection: React.FC = () => {
  const visionPoints = [
    'Establish Odisha as India\'s premier sporting state',
    'Create a pipeline of world-class athletes',
    'Build sustainable sporting infrastructure',
    'Foster a culture of excellence and sportsmanship'
  ];

  const missionGoals = [
    {
      icon: GlobeAltIcon,
      title: 'Comprehensive Coverage',
      description: 'Reach every block and village across Odisha to identify hidden talent'
    },
    {
      icon: SparklesIcon,
      title: 'World-Class Training',
      description: 'Provide international standard coaching and facilities to all participants'
    },
    {
      icon: BoltIcon,
      title: 'Performance Excellence',
      description: 'Develop athletes capable of competing at national and international levels'
    }
  ];

  return (
    <section id="vision" className="py-20 bg-white">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16 animate-slide-up">
          <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6">
            Our <span className="text-primary-600">Vision</span> & 
            <span className="text-success-600"> Mission</span>
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-primary-600 to-success-600 mx-auto mb-6"></div>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Guided by ambitious goals and driven by the passion to excel, 
            we're building the future of sports in Odisha.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 mb-20">
          {/* Vision Section */}
          <div className="space-y-8 animate-slide-up">
            <div className="flex items-center space-x-4 mb-6">
              <div className="w-16 h-16 bg-gradient-to-r from-primary-600 to-primary-700 rounded-xl flex items-center justify-center">
                <EyeIcon className="w-8 h-8 text-white" />
              </div>
              <div>
                <h3 className="text-3xl font-bold text-gray-900">Our Vision</h3>
                <p className="text-primary-600 font-semibold">The Future We Envision</p>
              </div>
            </div>

            <div className="bg-gradient-to-r from-primary-50 to-primary-100 rounded-2xl p-8 border border-primary-200">
              <p className="text-lg text-gray-700 leading-relaxed mb-6 font-medium">
                "To transform Odisha into a national sporting powerhouse, nurturing talent 
                from every village and creating champions who bring pride to our state and 
                country on the global stage."
              </p>

              <div className="space-y-4">
                {visionPoints.map((point, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <CheckCircleIcon className="w-6 h-6 text-primary-600 mt-1 flex-shrink-0" />
                    <span className="text-gray-700">{point}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Vision Stats */}
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-100 text-center">
                <div className="text-2xl font-bold text-primary-600 mb-2">50,000+</div>
                <div className="text-gray-600 text-sm">Youth to be Trained</div>
              </div>
              <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-100 text-center">
                <div className="text-2xl font-bold text-success-600 mb-2">1000+</div>
                <div className="text-gray-600 text-sm">Elite Athletes Target</div>
              </div>
            </div>
          </div>

          {/* Mission Section */}
          <div className="space-y-8 animate-slide-up">
            <div className="flex items-center space-x-4 mb-6">
              <div className="w-16 h-16 bg-gradient-to-r from-success-600 to-success-700 rounded-xl flex items-center justify-center">
                <BoltIcon className="w-8 h-8 text-white" />
              </div>
              <div>
                <h3 className="text-3xl font-bold text-gray-900">Our Mission</h3>
                <p className="text-success-600 font-semibold">How We'll Achieve It</p>
              </div>
            </div>

            <div className="bg-gradient-to-r from-success-50 to-success-100 rounded-2xl p-8 border border-success-200">
              <p className="text-lg text-gray-700 leading-relaxed mb-8 font-medium">
                "To identify, train, and develop sporting talent across all 314 blocks of Odisha 
                through comprehensive programs, world-class coaching, and state-of-the-art facilities."
              </p>

              <div className="space-y-6">
                {missionGoals.map((goal, index) => (
                  <div key={index} className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-gradient-to-r from-success-600 to-success-700 rounded-lg flex items-center justify-center flex-shrink-0">
                        <goal.icon className="w-6 h-6 text-white" />
                      </div>
                      <div className="flex-1">
                        <h4 className="text-lg font-bold text-gray-900 mb-2">{goal.title}</h4>
                        <p className="text-gray-600">{goal.description}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Implementation Strategy */}
        <div className="bg-gray-50 rounded-2xl p-8 mb-16 animate-slide-up">
          <div className="text-center mb-8">
            <h3 className="text-3xl font-bold text-gray-900 mb-4">Implementation Strategy</h3>
            <p className="text-lg text-gray-600">Our systematic approach to achieving these goals</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {[
              { step: '01', title: 'Talent Identification', description: 'Systematic scouting across all blocks' },
              { step: '02', title: 'Skill Development', description: 'Structured training programs' },
              { step: '03', title: 'Performance Tracking', description: 'Continuous assessment and improvement' },
              { step: '04', title: 'Elite Preparation', description: 'Advanced training for competitions' }
            ].map((phase, index) => (
              <div key={index} className="relative">
                <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-100 text-center group hover:shadow-xl transition-all duration-300">
                  <div className="w-12 h-12 bg-gradient-to-r from-primary-600 to-success-600 rounded-full flex items-center justify-center text-white font-bold text-lg mx-auto mb-4 group-hover:scale-110 transition-transform">
                    {phase.step}
                  </div>
                  <h4 className="text-lg font-bold text-gray-900 mb-2">{phase.title}</h4>
                  <p className="text-gray-600 text-sm">{phase.description}</p>
                </div>
                
                {index < 3 && (
                  <div className="hidden md:block absolute top-1/2 -right-3 transform -translate-y-1/2 z-10">
                    <ArrowRightIcon className="w-6 h-6 text-gray-400" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Partnership */}
        <div className="text-center animate-slide-up">
          <div className="bg-gradient-to-r from-gray-900 to-gray-800 rounded-2xl p-8 text-white">
            <h3 className="text-3xl font-bold mb-4">Powered by Hardini Dynamics</h3>
            <p className="text-xl mb-6 text-gray-300">
              A landmark project that combines expertise, innovation, and passion 
              to create lasting impact in Odisha's sporting landscape.
            </p>
            <div className="flex items-center justify-center space-x-8 text-gray-400">
              <div className="text-center">
                <div className="text-2xl font-bold text-warning-400">15+</div>
                <div className="text-sm">Years Experience</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-success-400">100+</div>
                <div className="text-sm">Projects Delivered</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-primary-400">500+</div>
                <div className="text-sm">Lives Impacted</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default VisionMissionSection;